/**
 * Unified Material Estimator Data
 * Professional construction material estimation for early-stage planning
 * Supports multiple unit systems: Imperial (ft, inch) and Metric (m, cm)
 */

// ============================================
// UNIT SYSTEMS AND CONVERSION CONSTANTS
// ============================================

// Base conversion constants
export const CONVERSIONS = {
  // Area conversions (to sqft as base)
  SQM_TO_SQFT: 10.764,
  SQFT_TO_SQM: 0.0929,

  // Length conversions (to feet as base)
  M_TO_FT: 3.281,
  FT_TO_M: 0.3048,
  CM_TO_FT: 0.0328,
  INCH_TO_FT: 0.0833,
  FT_TO_INCH: 12,
  M_TO_INCH: 39.37,
  CM_TO_INCH: 0.3937,
  INCH_TO_M: 0.0254,
  INCH_TO_CM: 2.54,
  CM_TO_M: 0.01,
  M_TO_CM: 100,

  // Volume conversions
  CUFT_TO_CUM: 0.0283,
  CUM_TO_CUFT: 35.315
};

// Available unit options for user selection
export const UNIT_OPTIONS = {
  length: [
    { id: 'ft', label: 'Feet (ft)', labelMy: 'Kaki (ft)', toFeet: 1 },
    { id: 'inch', label: 'Inches (in)', labelMy: 'Inci (in)', toFeet: CONVERSIONS.INCH_TO_FT },
    { id: 'm', label: 'Metres (m)', labelMy: 'Meter (m)', toFeet: CONVERSIONS.M_TO_FT },
    { id: 'cm', label: 'Centimetres (cm)', labelMy: 'Sentimeter (cm)', toFeet: CONVERSIONS.CM_TO_FT }
  ],
  area: [
    { id: 'sqft', label: 'Square Feet (sqft)', labelMy: 'Kaki Persegi (sqft)', toSqft: 1 },
    { id: 'sqm', label: 'Square Metres (sqm)', labelMy: 'Meter Persegi (sqm)', toSqft: CONVERSIONS.SQM_TO_SQFT },
    { id: 'sqin', label: 'Square Inches (sqin)', labelMy: 'Inci Persegi (sqin)', toSqft: 1/144 }
  ],
  thickness: [
    { id: 'mm', label: 'Millimetres (mm)', labelMy: 'Milimeter (mm)', toMm: 1 },
    { id: 'cm', label: 'Centimetres (cm)', labelMy: 'Sentimeter (cm)', toMm: 10 },
    { id: 'inch', label: 'Inches (in)', labelMy: 'Inci (in)', toMm: 25.4 },
    { id: 'm', label: 'Metres (m)', labelMy: 'Meter (m)', toMm: 1000 }
  ]
};

// Helper function to convert length to feet
export const convertToFeet = (value, fromUnit) => {
  const unitOption = UNIT_OPTIONS.length.find(u => u.id === fromUnit);
  return value * (unitOption?.toFeet || 1);
};

// Helper function to convert area to sqft
export const convertToSqft = (value, fromUnit) => {
  const unitOption = UNIT_OPTIONS.area.find(u => u.id === fromUnit);
  return value * (unitOption?.toSqft || 1);
};

// Helper function to convert thickness to mm
export const convertToMm = (value, fromUnit) => {
  const unitOption = UNIT_OPTIONS.thickness.find(u => u.id === fromUnit);
  return value * (unitOption?.toMm || 1);
};

// Variance for estimates (±12%)
export const ESTIMATE_VARIANCE = 0.12;

/**
 * Material Categories
 */
export const CATEGORIES = {
  walls: {
    id: 'walls',
    label: 'Walls',
    labelMy: 'Dinding',
    icon: 'walls',
    description: 'Bricks, plastering, and painting',
    descriptionMy: 'Batu bata, plaster, dan cat'
  },
  floors: {
    id: 'floors',
    label: 'Floors',
    labelMy: 'Lantai',
    icon: 'floors',
    description: 'Tiles, screed, and concrete',
    descriptionMy: 'Jubin, screed, dan konkrit'
  },
  roofing: {
    id: 'roofing',
    label: 'Roofing',
    labelMy: 'Bumbung',
    icon: 'roofing',
    description: 'Metal sheets and concrete slabs',
    descriptionMy: 'Kepingan logam dan papak konkrit'
  },
  concrete: {
    id: 'concrete',
    label: 'Concrete Work',
    labelMy: 'Kerja Konkrit',
    icon: 'concrete',
    description: 'Slabs, beams, and foundations',
    descriptionMy: 'Papak, rasuk, dan asas'
  },
  ceiling: {
    id: 'ceiling',
    label: 'Ceiling',
    labelMy: 'Siling',
    icon: 'ceiling',
    description: 'Gypsum, PVC, and wood panels',
    descriptionMy: 'Panel gipsum, PVC, dan kayu'
  },
  earthwork: {
    id: 'earthwork',
    label: 'Earthwork',
    labelMy: 'Kerja Tanah',
    icon: 'earthwork',
    description: 'Embankment, excavation, and compaction',
    descriptionMy: 'Tambakan, penggalian, dan pemadatan'
  },
  surveying: {
    id: 'surveying',
    label: 'Land Surveying',
    labelMy: 'Ukur Tanah',
    icon: 'surveying',
    description: 'Site surveying and setting out',
    descriptionMy: 'Ukur tapak dan penetapan garisan'
  },
  foundation: {
    id: 'foundation',
    label: 'Foundation',
    labelMy: 'Asas',
    icon: 'foundation',
    description: 'Footings, piles, and retaining walls',
    descriptionMy: 'Tapak, cerucuk, dan dinding penahan'
  },
  plastering: {
    id: 'plastering',
    label: 'Plastering & Rendering',
    labelMy: 'Plaster & Lepa',
    icon: 'plastering',
    description: 'Internal and external plastering',
    descriptionMy: 'Plaster dalaman dan luaran'
  }
};

/**
 * Material Types by Category
 */
export const MATERIALS = {
  walls: {
    bricks: {
      id: 'bricks',
      label: 'Brick Wall (Stretcher Bond)',
      labelMy: 'Dinding Bata (Ikatan Stretcher)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Wall Length', labelMy: 'Panjang Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'height', label: 'Wall Height', labelMy: 'Tinggi Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'mainDoor', label: 'Main Doors (7\'×3\')', labelMy: 'Pintu Utama (7\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'roomDoor', label: 'Room Doors (7\'×2.5\')', labelMy: 'Pintu Bilik (7\'×2.5\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'bathroomDoor', label: 'Bathroom Doors (7\'×2\')', labelMy: 'Pintu Bilik Air (7\'×2\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'windows', label: 'Windows (4\'×3\')', labelMy: 'Tingkap (4\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 }
      ],
      calculate: (inputs, units = {}) => {
        // Convert inputs to feet
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const heightFt = convertToFeet(inputs.height || 0, units.length || 'ft');

        // Calculate wall area
        const wallAreaSqft = lengthFt * heightFt;

        // Deduct openings (standard sizes)
        const mainDoorArea = (inputs.mainDoor || 0) * 21; // 7ft × 3ft = 21 sqft
        const roomDoorArea = (inputs.roomDoor || 0) * 17.5; // 7ft × 2.5ft = 17.5 sqft
        const bathroomDoorArea = (inputs.bathroomDoor || 0) * 14; // 7ft × 2ft = 14 sqft
        const windowArea = (inputs.windows || 0) * 12; // 4ft × 3ft = 12 sqft

        const totalOpenings = mainDoorArea + roomDoorArea + bathroomDoorArea + windowArea;
        const netAreaSqft = wallAreaSqft - totalOpenings;

        if (netAreaSqft <= 0) return { primary: { value: 0, unit: 'pcs', unitMy: 'biji', label: 'Bricks', labelMy: 'Bata' }, secondary: [] };

        // Standard calculation (from Sukat app):
        // Bricks: 5 per sqft for stretcher bond (half brick wall)
        const bricksPerSqft = 5;
        const totalBricks = Math.round(netAreaSqft * bricksPerSqft * 10) / 10;

        // Bundle calculation (1 bundle = 600 pcs)
        const bundles = Math.round(totalBricks / 600 * 100) / 100;

        // Cement: 0.00281 bags per brick (or ~355 bricks per 50kg bag)
        const cementBags = Math.round(totalBricks * 0.00281 * 10) / 10;

        // Sand: 0.0004 m³ per brick
        const sandCum = Math.round(totalBricks * 0.0004 * 100) / 100;
        const sandCuft = Math.round(sandCum * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandCum * 1.5 * 100) / 100; // 1.5 ton per m³

        return {
          primary: {
            value: totalBricks,
            unit: 'pcs',
            unitMy: 'biji',
            label: 'Bricks',
            labelMy: 'Bata',
            extra: `${bundles} bundles (1 bundle = 600 pcs)`,
            extraMy: `${bundles} berkas (1 berkas = 600 biji)`
          },
          secondary: [
            { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
            { value: sandCum, unit: 'm³', unitMy: 'm³', label: 'Sand', labelMy: 'Pasir', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: 'Stretcher bond (half brick wall 4.5"), 5 bricks per sqft, 1 bundle = 600 pcs, sand ~1.5 ton/m³',
        my: 'Ikatan stretcher (dinding separuh bata 4.5"), 5 bata per kaki persegi, 1 berkas = 600 biji, pasir ~1.5 tan/m³'
      }
    },
    bricksFullWall: {
      id: 'bricksFullWall',
      label: 'Brick Wall (English Bond - 9")',
      labelMy: 'Dinding Bata (Ikatan Inggeris - 9")',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Wall Length', labelMy: 'Panjang Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'height', label: 'Wall Height', labelMy: 'Tinggi Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'mainDoor', label: 'Main Doors (7\'×3\')', labelMy: 'Pintu Utama (7\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'roomDoor', label: 'Room Doors (7\'×2.5\')', labelMy: 'Pintu Bilik (7\'×2.5\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'bathroomDoor', label: 'Bathroom Doors (7\'×2\')', labelMy: 'Pintu Bilik Air (7\'×2\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'windows', label: 'Windows (4\'×3\')', labelMy: 'Tingkap (4\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const heightFt = convertToFeet(inputs.height || 0, units.length || 'ft');

        const wallAreaSqft = lengthFt * heightFt;

        const mainDoorArea = (inputs.mainDoor || 0) * 21;
        const roomDoorArea = (inputs.roomDoor || 0) * 17.5;
        const bathroomDoorArea = (inputs.bathroomDoor || 0) * 14;
        const windowArea = (inputs.windows || 0) * 12;

        const totalOpenings = mainDoorArea + roomDoorArea + bathroomDoorArea + windowArea;
        const netAreaSqft = wallAreaSqft - totalOpenings;

        if (netAreaSqft <= 0) return { primary: { value: 0, unit: 'pcs', unitMy: 'biji', label: 'Bricks', labelMy: 'Bata' }, secondary: [] };

        // Full brick wall (9" thick) = 10 bricks per sqft (double layer)
        const bricksPerSqft = 10;
        const totalBricks = Math.round(netAreaSqft * bricksPerSqft * 10) / 10;

        // Bundle calculation (1 bundle = 600 pcs)
        const bundles = Math.round(totalBricks / 600 * 100) / 100;

        // Cement and sand proportionally increased
        const cementBags = Math.round(totalBricks * 0.00281 * 10) / 10;
        const sandCum = Math.round(totalBricks * 0.0004 * 100) / 100;
        const sandCuft = Math.round(sandCum * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandCum * 1.5 * 100) / 100; // 1.5 ton per m³

        return {
          primary: {
            value: totalBricks,
            unit: 'pcs',
            unitMy: 'biji',
            label: 'Bricks',
            labelMy: 'Bata',
            extra: `${bundles} bundles (1 bundle = 600 pcs)`,
            extraMy: `${bundles} berkas (1 berkas = 600 biji)`
          },
          secondary: [
            { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
            { value: sandCum, unit: 'm³', unitMy: 'm³', label: 'Sand', labelMy: 'Pasir', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: 'English bond (full brick wall 9"), 10 bricks per sqft, 1 bundle = 600 pcs, sand ~1.5 ton/m³',
        my: 'Ikatan Inggeris (dinding bata penuh 9"), 10 bata per kaki persegi, 1 berkas = 600 biji, pasir ~1.5 tan/m³'
      }
    },
    bricksByArea: {
      id: 'bricksByArea',
      label: 'Bricks (Direct Area Input)',
      labelMy: 'Bata (Input Luas Terus)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'area', label: 'Net Wall Area', labelMy: 'Luas Bersih Dinding', unit: 'sqft', unitMy: 'kaki persegi', unitType: 'area' }
      ],
      calculate: (inputs, units = {}) => {
        const areaSqft = convertToSqft(inputs.area || 0, units.area || 'sqft');

        if (areaSqft <= 0) return { primary: { value: 0, unit: 'pcs', unitMy: 'biji', label: 'Bricks', labelMy: 'Bata' }, secondary: [] };

        // 5 bricks per sqft (stretcher bond)
        const totalBricks = Math.round(areaSqft * 5 * 10) / 10;

        // Bundle calculation (1 bundle = 600 pcs)
        const bundles = Math.round(totalBricks / 600 * 100) / 100;

        const cementBags = Math.round(totalBricks * 0.00281 * 10) / 10;
        const sandCum = Math.round(totalBricks * 0.0004 * 100) / 100;
        const sandCuft = Math.round(sandCum * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandCum * 1.5 * 100) / 100; // 1.5 ton per m³

        return {
          primary: {
            value: totalBricks,
            unit: 'pcs',
            unitMy: 'biji',
            label: 'Bricks',
            labelMy: 'Bata',
            extra: `${bundles} bundles (1 bundle = 600 pcs)`,
            extraMy: `${bundles} berkas (1 berkas = 600 biji)`
          },
          secondary: [
            { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
            { value: sandCum, unit: 'm³', unitMy: 'm³', label: 'Sand', labelMy: 'Pasir', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: 'Stretcher bond, 5 bricks per sqft, 1 bundle = 600 pcs, sand ~1.5 ton/m³',
        my: 'Ikatan stretcher, 5 bata per kaki persegi, 1 berkas = 600 biji, pasir ~1.5 tan/m³'
      }
    },
    plaster: {
      id: 'plaster',
      label: 'Plastering',
      labelMy: 'Plaster',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Wall Length', labelMy: 'Panjang Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'height', label: 'Wall Height', labelMy: 'Tinggi Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'mainDoor', label: 'Main Doors (7\'×3\')', labelMy: 'Pintu Utama (7\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'roomDoor', label: 'Room Doors (7\'×2.5\')', labelMy: 'Pintu Bilik (7\'×2.5\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'bathroomDoor', label: 'Bathroom Doors (7\'×2\')', labelMy: 'Pintu Bilik Air (7\'×2\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'windows', label: 'Windows (4\'×3\')', labelMy: 'Tingkap (4\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const heightFt = convertToFeet(inputs.height || 0, units.length || 'ft');

        // Calculate wall area
        const wallAreaSqft = lengthFt * heightFt;

        // Deduct openings (standard sizes)
        const mainDoorArea = (inputs.mainDoor || 0) * 21;
        const roomDoorArea = (inputs.roomDoor || 0) * 17.5;
        const bathroomDoorArea = (inputs.bathroomDoor || 0) * 14;
        const windowArea = (inputs.windows || 0) * 12;

        const totalOpenings = mainDoorArea + roomDoorArea + bathroomDoorArea + windowArea;
        const netAreaSqft = wallAreaSqft - totalOpenings;

        if (netAreaSqft <= 0) return { primary: { value: 0, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' }, secondary: [] };

        // Standard plaster calculation (from Sukat app):
        // Cement: ~0.01129 bags per sqft (or ~88.5 sqft per bag)
        // Sand: ~0.00169 m³ per sqft
        const cementBags = Math.round(netAreaSqft * 0.01129 * 10) / 10;
        const sandCum = Math.round(netAreaSqft * 0.00169 * 100) / 100;
        const sandCuft = Math.round(sandCum * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandCum * 1.5 * 100) / 100; // fine sand ~1.5 ton/m³

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sandCum, unit: 'm³', unitMy: 'm³', label: 'Sand', labelMy: 'Pasir', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: 'Internal plaster (~12-15mm), 1:4 cement-sand ratio, sand ~1.5 ton/m³',
        my: 'Plaster dalaman (~12-15mm), nisbah simen-pasir 1:4, pasir ~1.5 tan/m³'
      }
    },
    plasterByArea: {
      id: 'plasterByArea',
      label: 'Plastering (Direct Area)',
      labelMy: 'Plaster (Luas Terus)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'area', label: 'Net Wall Area', labelMy: 'Luas Bersih Dinding', unit: 'sqft', unitMy: 'kaki persegi', unitType: 'area' }
      ],
      calculate: (inputs, units = {}) => {
        const areaSqft = convertToSqft(inputs.area || 0, units.area || 'sqft');

        if (areaSqft <= 0) return { primary: { value: 0, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' }, secondary: [] };

        const cementBags = Math.round(areaSqft * 0.01129 * 10) / 10;
        const sandCum = Math.round(areaSqft * 0.00169 * 100) / 100;
        const sandCuft = Math.round(sandCum * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandCum * 1.5 * 100) / 100; // fine sand ~1.5 ton/m³

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sandCum, unit: 'm³', unitMy: 'm³', label: 'Sand', labelMy: 'Pasir', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: 'Internal plaster, 1:4 ratio, sand ~1.5 ton/m³',
        my: 'Plaster dalaman, nisbah 1:4, pasir ~1.5 tan/m³'
      }
    },
    paint: {
      id: 'paint',
      label: 'Paint',
      labelMy: 'Cat',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Wall Length', labelMy: 'Panjang Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'height', label: 'Wall Height', labelMy: 'Tinggi Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'mainDoor', label: 'Main Doors (7\'×3\')', labelMy: 'Pintu Utama (7\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'roomDoor', label: 'Room Doors (7\'×2.5\')', labelMy: 'Pintu Bilik (7\'×2.5\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'bathroomDoor', label: 'Bathroom Doors (7\'×2\')', labelMy: 'Pintu Bilik Air (7\'×2\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'windows', label: 'Windows (4\'×3\')', labelMy: 'Tingkap (4\'×3\')', unit: 'pcs', unitMy: 'unit', default: 0 },
        { id: 'coats', label: 'Number of Coats', labelMy: 'Bilangan Lapisan', unit: '', unitMy: '', default: 2 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const heightFt = convertToFeet(inputs.height || 0, units.length || 'ft');
        const coats = inputs.coats || 2;

        // Calculate wall area
        const wallAreaSqft = lengthFt * heightFt;

        // Deduct openings (standard sizes)
        const mainDoorArea = (inputs.mainDoor || 0) * 21;
        const roomDoorArea = (inputs.roomDoor || 0) * 17.5;
        const bathroomDoorArea = (inputs.bathroomDoor || 0) * 14;
        const windowArea = (inputs.windows || 0) * 12;

        const totalOpenings = mainDoorArea + roomDoorArea + bathroomDoorArea + windowArea;
        const netAreaSqft = wallAreaSqft - totalOpenings;

        if (netAreaSqft <= 0) return { primary: { value: 0, unit: 'liters', unitMy: 'liter', label: 'Paint', labelMy: 'Cat' }, secondary: [] };

        // Coverage: ~100 sqft per liter per coat (standard emulsion paint)
        const liters = Math.ceil((netAreaSqft * coats) / 100);
        return {
          primary: { value: liters, unit: 'liters', unitMy: 'liter', label: 'Paint', labelMy: 'Cat' },
          secondary: [
            { value: Math.ceil(liters * 0.1), unit: 'liters', unitMy: 'liter', label: 'Primer', labelMy: 'Pendasar' }
          ]
        };
      },
      assumptions: {
        en: 'Emulsion paint, smooth plastered surface, ~100 sqft coverage per liter per coat, plus 10% primer',
        my: 'Cat emulsi, permukaan plaster licin, ~100 kaki persegi liputan per liter per lapisan, tambah 10% pendasar'
      }
    },
    paintByArea: {
      id: 'paintByArea',
      label: 'Paint (Direct Area)',
      labelMy: 'Cat (Luas Terus)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'area', label: 'Net Wall Area', labelMy: 'Luas Bersih Dinding', unit: 'sqft', unitMy: 'kaki persegi', unitType: 'area' },
        { id: 'coats', label: 'Number of Coats', labelMy: 'Bilangan Lapisan', unit: '', unitMy: '', default: 2 }
      ],
      calculate: (inputs, units = {}) => {
        const areaSqft = convertToSqft(inputs.area || 0, units.area || 'sqft');
        const coats = inputs.coats || 2;

        if (areaSqft <= 0) return { primary: { value: 0, unit: 'liters', unitMy: 'liter', label: 'Paint', labelMy: 'Cat' }, secondary: [] };

        // Coverage: ~100 sqft per liter per coat
        const liters = Math.ceil((areaSqft * coats) / 100);
        return {
          primary: { value: liters, unit: 'liters', unitMy: 'liter', label: 'Paint', labelMy: 'Cat' },
          secondary: [
            { value: Math.ceil(liters * 0.1), unit: 'liters', unitMy: 'liter', label: 'Primer', labelMy: 'Pendasar' }
          ]
        };
      },
      assumptions: {
        en: 'Emulsion paint, enter net area (after deducting openings), ~100 sqft per liter per coat',
        my: 'Cat emulsi, masukkan luas bersih (selepas tolak bukaan), ~100 kaki persegi per liter per lapisan'
      }
    }
  },

  floors: {
    tiles: {
      id: 'tiles',
      label: 'Floor Tiles',
      labelMy: 'Jubin Lantai',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Room Length', labelMy: 'Panjang Bilik', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Room Width', labelMy: 'Lebar Bilik', unit: 'ft', unitMy: 'kaki', unitType: 'length' }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const areaSqft = lengthFt * widthFt;

        // 10% wastage for cutting
        const tiles = Math.ceil(areaSqft * 1.1);
        const adhesive = Math.ceil(areaSqft * 0.05); // ~5kg per sqft
        const grout = Math.ceil(areaSqft * 0.02); // ~0.5kg per sqft

        return {
          primary: { value: tiles, unit: 'sqft', unitMy: 'kaki persegi', label: 'Tiles', labelMy: 'Jubin' },
          secondary: [
            { value: Math.ceil(adhesive / 20), unit: 'bags (20kg)', unitMy: 'beg (20kg)', label: 'Tile Adhesive', labelMy: 'Pelekat Jubin' },
            { value: grout, unit: 'kg', unitMy: 'kg', label: 'Grout', labelMy: 'Grout' }
          ]
        };
      },
      assumptions: {
        en: 'Standard 12"×12" ceramic tiles, includes 10% wastage for cutting and breakage',
        my: 'Jubin seramik standard 12"×12", termasuk 10% pembaziran untuk pemotongan dan pecah'
      }
    },
    tilesByArea: {
      id: 'tilesByArea',
      label: 'Floor Tiles (Direct Area)',
      labelMy: 'Jubin Lantai (Luas Terus)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'area', label: 'Floor Area', labelMy: 'Luas Lantai', unit: 'sqft', unitMy: 'kaki persegi', unitType: 'area' }
      ],
      calculate: (inputs, units = {}) => {
        const areaSqft = convertToSqft(inputs.area || 0, units.area || 'sqft');

        const tiles = Math.ceil(areaSqft * 1.1);
        const adhesive = Math.ceil(areaSqft * 0.05);
        const grout = Math.ceil(areaSqft * 0.02);

        return {
          primary: { value: tiles, unit: 'sqft', unitMy: 'kaki persegi', label: 'Tiles', labelMy: 'Jubin' },
          secondary: [
            { value: Math.ceil(adhesive / 20), unit: 'bags (20kg)', unitMy: 'beg (20kg)', label: 'Tile Adhesive', labelMy: 'Pelekat Jubin' },
            { value: grout, unit: 'kg', unitMy: 'kg', label: 'Grout', labelMy: 'Grout' }
          ]
        };
      },
      assumptions: {
        en: 'Standard ceramic tiles, includes 10% wastage',
        my: 'Jubin seramik standard, termasuk 10% pembaziran'
      }
    },
    screed: {
      id: 'screed',
      label: 'Floor Screed',
      labelMy: 'Screed Lantai',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Floor Length', labelMy: 'Panjang Lantai', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Floor Width', labelMy: 'Lebar Lantai', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'thickness', label: 'Screed Thickness', labelMy: 'Ketebalan Screed', unit: 'mm', unitMy: 'mm', unitType: 'thickness', default: 25 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const thicknessMm = convertToMm(inputs.thickness || 25, units.thickness || 'mm');

        const areaSqft = lengthFt * widthFt;
        const areaSqm = areaSqft / CONVERSIONS.SQM_TO_SQFT;
        const thicknessM = thicknessMm / 1000;
        const volume = areaSqm * thicknessM * 1.1; // 10% wastage

        // 1:3 ratio (cement:sand)
        const cementBags = Math.ceil(volume * (1/4) * 1440 / 50);
        const sand = Math.round(volume * (3/4) * 1.2 * 100) / 100;
        const sandCuft = Math.round(sand * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sand * 1.5 * 100) / 100; // fine sand ~1.5 ton/m³

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (fine)', labelMy: 'Pasir (halus)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: '1:3 cement-sand ratio, sand ~1.5 ton/m³, includes 10% wastage',
        my: 'Nisbah simen-pasir 1:3, pasir ~1.5 tan/m³, termasuk 10% pembaziran'
      }
    },
    screedByArea: {
      id: 'screedByArea',
      label: 'Floor Screed (Direct Area)',
      labelMy: 'Screed Lantai (Luas Terus)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'area', label: 'Floor Area', labelMy: 'Luas Lantai', unit: 'sqft', unitMy: 'kaki persegi', unitType: 'area' },
        { id: 'thickness', label: 'Screed Thickness', labelMy: 'Ketebalan Screed', unit: 'mm', unitMy: 'mm', unitType: 'thickness', default: 25 }
      ],
      calculate: (inputs, units = {}) => {
        const areaSqft = convertToSqft(inputs.area || 0, units.area || 'sqft');
        const thicknessMm = convertToMm(inputs.thickness || 25, units.thickness || 'mm');

        const areaSqm = areaSqft / CONVERSIONS.SQM_TO_SQFT;
        const thicknessM = thicknessMm / 1000;
        const volume = areaSqm * thicknessM * 1.1;

        const cementBags = Math.ceil(volume * (1/4) * 1440 / 50);
        const sand = Math.round(volume * (3/4) * 1.2 * 100) / 100;
        const sandCuft = Math.round(sand * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sand * 1.5 * 100) / 100; // fine sand ~1.5 ton/m³

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (fine)', labelMy: 'Pasir (halus)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: '1:3 cement-sand ratio, sand ~1.5 ton/m³, includes 10% wastage',
        my: 'Nisbah simen-pasir 1:3, pasir ~1.5 tan/m³, termasuk 10% pembaziran'
      }
    }
  },

  roofing: {
    metalSheet: {
      id: 'metalSheet',
      label: 'Metal Roofing',
      labelMy: 'Bumbung Logam',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Roof Length', labelMy: 'Panjang Bumbung', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Roof Width', labelMy: 'Lebar Bumbung', unit: 'ft', unitMy: 'kaki', unitType: 'length' }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const areaSqft = lengthFt * widthFt;

        // 15% extra for overlaps
        const sheets = Math.ceil(areaSqft * 1.15);
        const screws = Math.ceil(areaSqft * 0.5);

        return {
          primary: { value: sheets, unit: 'sqft', unitMy: 'kaki persegi', label: 'Metal Sheets', labelMy: 'Kepingan Logam' },
          secondary: [
            { value: screws, unit: 'pieces', unitMy: 'keping', label: 'Roofing Screws', labelMy: 'Skru Bumbung' }
          ]
        };
      },
      assumptions: {
        en: 'Metal deck/zinc sheets, includes 15% for overlaps and ridges',
        my: 'Kepingan dek logam/zink, termasuk 15% untuk pertindihan dan rabung'
      }
    },
    metalSheetByArea: {
      id: 'metalSheetByArea',
      label: 'Metal Roofing (Direct Area)',
      labelMy: 'Bumbung Logam (Luas Terus)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'area', label: 'Roof Area', labelMy: 'Luas Bumbung', unit: 'sqft', unitMy: 'kaki persegi', unitType: 'area' }
      ],
      calculate: (inputs, units = {}) => {
        const areaSqft = convertToSqft(inputs.area || 0, units.area || 'sqft');

        const sheets = Math.ceil(areaSqft * 1.15);
        const screws = Math.ceil(areaSqft * 0.5);

        return {
          primary: { value: sheets, unit: 'sqft', unitMy: 'kaki persegi', label: 'Metal Sheets', labelMy: 'Kepingan Logam' },
          secondary: [
            { value: screws, unit: 'pieces', unitMy: 'keping', label: 'Roofing Screws', labelMy: 'Skru Bumbung' }
          ]
        };
      },
      assumptions: {
        en: 'Metal deck/zinc sheets, includes 15% for overlaps',
        my: 'Kepingan dek logam/zink, termasuk 15% untuk pertindihan'
      }
    },
    concreteSlab: {
      id: 'concreteSlab',
      label: 'Concrete Roof Slab (M20)',
      labelMy: 'Papak Bumbung Konkrit (M20)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Roof Length', labelMy: 'Panjang Bumbung', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Roof Width', labelMy: 'Lebar Bumbung', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'thickness', label: 'Slab Thickness', labelMy: 'Ketebalan Papak', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 5 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const thicknessMm = convertToMm(inputs.thickness || 5, units.thickness || 'inch');

        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;
        const widthM = widthFt / CONVERSIONS.M_TO_FT;
        const thicknessM = thicknessMm / 1000;

        const wetVolume = lengthM * widthM * thicknessM;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M20: 1:1.5:3
        const cementVolume = (dryVolume / 5.5) * 1 * wastage;
        const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
        const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³
        const areaSqm = lengthM * widthM;
        const steel = Math.round(areaSqm * 6 * 10) / 10; // ~6kg/sqm for roof slab

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Reinforcement', labelMy: 'Tetulang Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M20 Grade (1:1.5:3), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M20 (1:1.5:3), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    }
  },

  concrete: {
    slab: {
      id: 'slab',
      label: 'Concrete Slab (M20 Grade)',
      labelMy: 'Papak Konkrit (Gred M20)',
      inputType: 'volume',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'thickness', label: 'Thickness', labelMy: 'Ketebalan', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 4 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const thicknessMm = convertToMm(inputs.thickness || 4, units.thickness || 'inch');

        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;
        const widthM = widthFt / CONVERSIONS.M_TO_FT;
        const thicknessM = thicknessMm / 1000;

        const wetVolume = lengthM * widthM * thicknessM;
        const dryVolume = wetVolume * 1.54; // Conversion factor for dry volume
        const wastage = 1.05; // 5% wastage

        // M20 Grade: 1:1.5:3 ratio (Cement:Sand:Stone/Aggregate)
        // Total parts = 1 + 1.5 + 3 = 5.5
        const cementVolume = (dryVolume / 5.5) * 1 * wastage;
        const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
        const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50); // 1440 kg/m³ density
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³

        // Steel for slab: ~4-5 kg per sqft of slab area
        const areaSqm = lengthM * widthM;
        const steel = Math.round(areaSqm * 5 * 10) / 10;

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Reinforcement', labelMy: 'Tetulang Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M20 Grade (1:1.5:3), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M20 (1:1.5:3), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    slabM15: {
      id: 'slabM15',
      label: 'Concrete Slab (M15 Grade)',
      labelMy: 'Papak Konkrit (Gred M15)',
      inputType: 'volume',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'thickness', label: 'Thickness', labelMy: 'Ketebalan', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 4 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const thicknessMm = convertToMm(inputs.thickness || 4, units.thickness || 'inch');

        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;
        const widthM = widthFt / CONVERSIONS.M_TO_FT;
        const thicknessM = thicknessMm / 1000;

        const wetVolume = lengthM * widthM * thicknessM;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M15 Grade: 1:2:4 ratio (Cement:Sand:Stone)
        // Total parts = 1 + 2 + 4 = 7
        const cementVolume = (dryVolume / 7) * 1 * wastage;
        const sandVolume = (dryVolume / 7) * 2 * wastage;
        const stoneVolume = (dryVolume / 7) * 4 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³

        const areaSqm = lengthM * widthM;
        const steel = Math.round(areaSqm * 4 * 10) / 10;

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Reinforcement', labelMy: 'Tetulang Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M15 Grade (1:2:4), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M15 (1:2:4), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    pccFloor: {
      id: 'pccFloor',
      label: 'PCC Floor (Plain Cement Concrete)',
      labelMy: 'Lantai PCC (Konkrit Simen Biasa)',
      inputType: 'volume',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'thickness', label: 'Thickness', labelMy: 'Ketebalan', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 3 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const thicknessMm = convertToMm(inputs.thickness || 3, units.thickness || 'inch');

        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;
        const widthM = widthFt / CONVERSIONS.M_TO_FT;
        const thicknessM = thicknessMm / 1000;

        const wetVolume = lengthM * widthM * thicknessM;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // PCC: 1:4:8 ratio (Cement:Sand:Stone) - for base/leveling
        // Total parts = 1 + 4 + 8 = 13
        const cementVolume = (dryVolume / 13) * 1 * wastage;
        const sandVolume = (dryVolume / 13) * 4 * wastage;
        const stoneVolume = (dryVolume / 13) * 8 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (40mm)', labelMy: 'Batu/Agregat (40mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: 'PCC 1:4:8 ratio, sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'PCC nisbah 1:4:8, pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    column: {
      id: 'column',
      label: 'Concrete Column (M20)',
      labelMy: 'Tiang Konkrit (M20)',
      inputType: 'dimensions',
      unitSelectable: true,
      inputs: [
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 9 },
        { id: 'depth', label: 'Depth', labelMy: 'Kedalaman', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 9 },
        { id: 'height', label: 'Height', labelMy: 'Tinggi', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'quantity', label: 'Number of Columns', labelMy: 'Bilangan Tiang', unit: '', unitMy: '', default: 1 }
      ],
      calculate: (inputs, units = {}) => {
        const widthMm = convertToMm(inputs.width || 9, units.thickness || 'inch');
        const depthMm = convertToMm(inputs.depth || 9, units.thickness || 'inch');
        const heightFt = convertToFeet(inputs.height || 0, units.length || 'ft');
        const qty = inputs.quantity || 1;

        const widthM = widthMm / 1000;
        const depthM = depthMm / 1000;
        const heightM = heightFt / CONVERSIONS.M_TO_FT;

        const wetVolume = widthM * depthM * heightM * qty;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M20: 1:1.5:3 ratio
        const cementVolume = (dryVolume / 5.5) * 1 * wastage;
        const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
        const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³
        const steel = Math.round(wetVolume * 100 * 10) / 10; // ~100kg/m³

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Bars', labelMy: 'Bar Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M20 Grade (1:1.5:3), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M20 (1:1.5:3), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    beam: {
      id: 'beam',
      label: 'Concrete Beam (M20)',
      labelMy: 'Rasuk Konkrit (M20)',
      inputType: 'dimensions',
      unitSelectable: true,
      inputs: [
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 9 },
        { id: 'depth', label: 'Depth', labelMy: 'Kedalaman', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 12 },
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'quantity', label: 'Number of Beams', labelMy: 'Bilangan Rasuk', unit: '', unitMy: '', default: 1 }
      ],
      calculate: (inputs, units = {}) => {
        const widthMm = convertToMm(inputs.width || 9, units.thickness || 'inch');
        const depthMm = convertToMm(inputs.depth || 12, units.thickness || 'inch');
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const qty = inputs.quantity || 1;

        const widthM = widthMm / 1000;
        const depthM = depthMm / 1000;
        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;

        const wetVolume = widthM * depthM * lengthM * qty;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M20: 1:1.5:3
        const cementVolume = (dryVolume / 5.5) * 1 * wastage;
        const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
        const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³
        const steel = Math.round(wetVolume * 120 * 10) / 10; // ~120kg/m³ for beams

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Bars', labelMy: 'Bar Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M20 Grade (1:1.5:3), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M20 (1:1.5:3), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    lintel: {
      id: 'lintel',
      label: 'Lintel Beam (above doors/windows)',
      labelMy: 'Rasuk Ambang (atas pintu/tingkap)',
      inputType: 'dimensions',
      unitSelectable: true,
      inputs: [
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 4.5 },
        { id: 'depth', label: 'Depth', labelMy: 'Kedalaman', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 6 },
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'quantity', label: 'Number of Lintels', labelMy: 'Bilangan Ambang', unit: '', unitMy: '', default: 1 }
      ],
      calculate: (inputs, units = {}) => {
        const widthMm = convertToMm(inputs.width || 4.5, units.thickness || 'inch');
        const depthMm = convertToMm(inputs.depth || 6, units.thickness || 'inch');
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const qty = inputs.quantity || 1;

        const widthM = widthMm / 1000;
        const depthM = depthMm / 1000;
        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;

        const wetVolume = widthM * depthM * lengthM * qty;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M15: 1:2:4 for lintels
        const cementVolume = (dryVolume / 7) * 1 * wastage;
        const sandVolume = (dryVolume / 7) * 2 * wastage;
        const stoneVolume = (dryVolume / 7) * 4 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³
        const steel = Math.round(wetVolume * 80 * 10) / 10;

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (12mm)', labelMy: 'Batu/Agregat (12mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Bars', labelMy: 'Bar Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M15 Grade (1:2:4), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M15 (1:2:4), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    }
  },

  ceiling: {
    gypsum: {
      id: 'gypsum',
      label: 'Gypsum Board',
      labelMy: 'Papan Gipsum',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Ceiling Area', labelMy: 'Luas Siling', unit: 'sqft', unitMy: 'kaki persegi' }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        // Standard board: 4ft x 8ft = 32 sqft
        const boards = Math.ceil((area * 1.1) / 32); // 10% wastage
        const screws = Math.ceil(area * 1.5); // ~1.5 screws per sqft
        const compound = Math.round(boards * 0.5 * 10) / 10; // 0.5kg per board
        return {
          primary: { value: boards, unit: 'sheets (4\'×8\')', unitMy: 'keping (4\'×8\')', label: 'Gypsum Board', labelMy: 'Papan Gipsum' },
          secondary: [
            { value: screws, unit: 'pieces', unitMy: 'keping', label: 'Screws', labelMy: 'Skru' },
            { value: compound, unit: 'kg', unitMy: 'kg', label: 'Joint Compound', labelMy: 'Sebatian Sendi' }
          ]
        };
      },
      assumptions: {
        en: 'Standard 4\'×8\' gypsum boards, metal frame system, includes 10% wastage',
        my: 'Papan gipsum standard 4\'×8\', sistem rangka logam, termasuk 10% pembaziran'
      }
    },
    pvc: {
      id: 'pvc',
      label: 'PVC Ceiling',
      labelMy: 'Siling PVC',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Ceiling Area', labelMy: 'Luas Siling', unit: 'sqft', unitMy: 'kaki persegi' }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const panels = Math.ceil(area * 1.1); // in sqft, 10% wastage
        const trim = Math.ceil(Math.sqrt(area) * 4 * 1.1); // perimeter estimate
        return {
          primary: { value: panels, unit: 'sqft', unitMy: 'kaki persegi', label: 'PVC Panels', labelMy: 'Panel PVC' },
          secondary: [
            { value: trim, unit: 'ft', unitMy: 'kaki', label: 'Edge Trim', labelMy: 'Trim Tepi' }
          ]
        };
      },
      assumptions: {
        en: 'Standard PVC ceiling panels, includes 10% wastage',
        my: 'Panel siling PVC standard, termasuk 10% pembaziran'
      }
    }
  },

  earthwork: {
    embankment: {
      id: 'embankment',
      label: 'Earth Filling (Embankment)',
      labelMy: 'Tambakan Tanah',
      inputType: 'volume',
      inputs: [
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki' },
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'ft', unitMy: 'kaki' },
        { id: 'height', label: 'Fill Height', labelMy: 'Tinggi Tambakan', unit: 'ft', unitMy: 'kaki' }
      ],
      calculate: (inputs) => {
        const length = (inputs.length || 0) / CONVERSIONS.M_TO_FT;
        const width = (inputs.width || 0) / CONVERSIONS.M_TO_FT;
        const height = (inputs.height || 0) / CONVERSIONS.M_TO_FT;
        const volume = length * width * height;
        // Add 25% for compaction (soil shrinks when compacted)
        const fillVolume = Math.round(volume * 1.25 * 10) / 10;
        const fillCuft = Math.round(fillVolume * CONVERSIONS.CUM_TO_CUFT * 10) / 10;
        const fillTon = Math.round(fillVolume * 1.7 * 10) / 10; // laterite/earth ~1.7 ton/m³
        // Estimate lorry loads (1 lorry = ~5m³)
        const lorryLoads = Math.ceil(fillVolume / 5);
        return {
          primary: { value: fillVolume, unit: 'm³', unitMy: 'm³', label: 'Fill Material', labelMy: 'Bahan Tambakan', extra: `${fillTon} ton / ${fillCuft} cuft`, extraMy: `${fillTon} tan / ${fillCuft} kaki padu` },
          secondary: [
            { value: lorryLoads, unit: 'trips', unitMy: 'trip', label: 'Lorry Loads (5m³)', labelMy: 'Muatan Lori (5m³)' }
          ]
        };
      },
      assumptions: {
        en: 'Includes 25% extra for compaction, laterite/red earth ~1.7 ton/m³',
        my: 'Termasuk 25% tambahan untuk pemadatan, tanah merah/laterit ~1.7 tan/m³'
      }
    },
    excavation: {
      id: 'excavation',
      label: 'Earth Cutting (Excavation)',
      labelMy: 'Penggalian Tanah',
      inputType: 'volume',
      inputs: [
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki' },
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'ft', unitMy: 'kaki' },
        { id: 'depth', label: 'Excavation Depth', labelMy: 'Kedalaman Gali', unit: 'ft', unitMy: 'kaki' }
      ],
      calculate: (inputs) => {
        const length = (inputs.length || 0) / CONVERSIONS.M_TO_FT;
        const width = (inputs.width || 0) / CONVERSIONS.M_TO_FT;
        const depth = (inputs.depth || 0) / CONVERSIONS.M_TO_FT;
        const volume = length * width * depth;
        // Add 20% for bulking (excavated soil expands)
        const excavatedVolume = Math.round(volume * 1.2 * 10) / 10;
        const excavatedCuft = Math.round(excavatedVolume * CONVERSIONS.CUM_TO_CUFT * 10) / 10;
        const excavatedTon = Math.round(excavatedVolume * 1.6 * 10) / 10; // soil ~1.6 ton/m³
        const lorryLoads = Math.ceil(excavatedVolume / 5);
        return {
          primary: { value: excavatedVolume, unit: 'm³', unitMy: 'm³', label: 'Excavated Material', labelMy: 'Bahan Digali', extra: `${excavatedTon} ton / ${excavatedCuft} cuft`, extraMy: `${excavatedTon} tan / ${excavatedCuft} kaki padu` },
          secondary: [
            { value: lorryLoads, unit: 'trips', unitMy: 'trip', label: 'Disposal Lorry Loads', labelMy: 'Muatan Lori Buang' }
          ]
        };
      },
      assumptions: {
        en: 'Includes 20% bulking factor, soil ~1.6 ton/m³',
        my: 'Termasuk 20% faktor pengembangan, tanah ~1.6 tan/m³'
      }
    },
    compaction: {
      id: 'compaction',
      label: 'Soil Compaction',
      labelMy: 'Pemadatan Tanah',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Area to Compact', labelMy: 'Luas Pemadatan', unit: 'sqft', unitMy: 'kaki persegi' },
        { id: 'layers', label: 'Number of Layers', labelMy: 'Bilangan Lapisan', unit: '', unitMy: '', default: 3 }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const layers = inputs.layers || 3;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        // Compaction passes per layer
        const totalPasses = Math.ceil(areaSqm * layers * 4); // 4 passes per layer
        // Water for compaction (~20 liters per sqm per layer)
        const water = Math.round(areaSqm * layers * 20);
        return {
          primary: { value: totalPasses, unit: 'passes', unitMy: 'laluan', label: 'Compactor Passes', labelMy: 'Laluan Pemadat' },
          secondary: [
            { value: water, unit: 'liters', unitMy: 'liter', label: 'Water for Compaction', labelMy: 'Air untuk Pemadatan' }
          ]
        };
      },
      assumptions: {
        en: '4 compactor passes per layer, ~200mm layer thickness',
        my: '4 laluan pemadat setiap lapisan, ketebalan lapisan ~200mm'
      }
    },
    grading: {
      id: 'grading',
      label: 'Site Grading/Leveling',
      labelMy: 'Merata Tapak',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Site Area', labelMy: 'Luas Tapak', unit: 'sqft', unitMy: 'kaki persegi' }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        // Machine hours estimate (bulldozer ~100sqm/hour)
        const machineHours = Math.ceil(areaSqm / 100);
        return {
          primary: { value: machineHours, unit: 'hours', unitMy: 'jam', label: 'Machine Hours', labelMy: 'Jam Mesin' },
          secondary: []
        };
      },
      assumptions: {
        en: 'Bulldozer/grader productivity ~100 sqm per hour',
        my: 'Produktiviti jentolak/penggerek ~100 m² sejam'
      }
    }
  },

  surveying: {
    landSurvey: {
      id: 'landSurvey',
      label: 'Land Survey',
      labelMy: 'Ukur Tanah',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Land Area', labelMy: 'Luas Tanah', unit: 'sqft', unitMy: 'kaki persegi' },
        { id: 'corners', label: 'Number of Corners/Points', labelMy: 'Bilangan Penjuru/Titik', unit: '', unitMy: '', default: 4 }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const corners = inputs.corners || 4;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        const areaAcre = areaSqm / 4047; // sqm to acre
        // Boundary markers/pegs
        const pegs = corners + Math.ceil(Math.sqrt(areaSqm) / 20); // extra pegs every 20m
        return {
          primary: { value: pegs, unit: 'pegs', unitMy: 'pancang', label: 'Survey Pegs', labelMy: 'Pancang Ukur' },
          secondary: [
            { value: Math.round(areaAcre * 100) / 100, unit: 'acres', unitMy: 'ekar', label: 'Land Area', labelMy: 'Luas Tanah' }
          ]
        };
      },
      assumptions: {
        en: 'Boundary survey with corner markers and intermediate pegs',
        my: 'Ukur sempadan dengan penanda penjuru dan pancang pertengahan'
      }
    },
    settingOut: {
      id: 'settingOut',
      label: 'Building Setting Out',
      labelMy: 'Penetapan Bangunan',
      inputType: 'dimensions',
      inputs: [
        { id: 'length', label: 'Building Length', labelMy: 'Panjang Bangunan', unit: 'ft', unitMy: 'kaki' },
        { id: 'width', label: 'Building Width', labelMy: 'Lebar Bangunan', unit: 'ft', unitMy: 'kaki' },
        { id: 'columns', label: 'Number of Column Points', labelMy: 'Bilangan Titik Tiang', unit: '', unitMy: '', default: 4 }
      ],
      calculate: (inputs) => {
        const length = inputs.length || 0;
        const width = inputs.width || 0;
        const columns = inputs.columns || 4;
        // Profile boards at corners and intermediate points
        const profileBoards = Math.ceil((columns + 4) * 2); // 2 per point
        // String lines
        const stringLines = Math.ceil((length + width) * 2 * 1.2 / CONVERSIONS.M_TO_FT); // perimeter + extra
        // Wooden pegs
        const pegs = columns + 8; // column points + 2 per corner
        return {
          primary: { value: profileBoards, unit: 'pieces', unitMy: 'keping', label: 'Profile Boards', labelMy: 'Papan Profil' },
          secondary: [
            { value: Math.ceil(stringLines), unit: 'm', unitMy: 'm', label: 'String Line', labelMy: 'Tali Garisan' },
            { value: pegs, unit: 'pieces', unitMy: 'keping', label: 'Wooden Pegs', labelMy: 'Pancang Kayu' }
          ]
        };
      },
      assumptions: {
        en: 'Standard building setting out with profile boards and string lines',
        my: 'Penetapan bangunan standard dengan papan profil dan tali garisan'
      }
    },
    topographicSurvey: {
      id: 'topographicSurvey',
      label: 'Topographic Survey',
      labelMy: 'Ukur Topografi',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Survey Area', labelMy: 'Luas Ukur', unit: 'sqft', unitMy: 'kaki persegi' }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        // Grid points (every 10m for detailed survey)
        const gridPoints = Math.ceil(areaSqm / 100); // 10m x 10m grid
        // Survey days estimate
        const surveyDays = Math.ceil(gridPoints / 50); // ~50 points per day
        return {
          primary: { value: gridPoints, unit: 'points', unitMy: 'titik', label: 'Survey Points', labelMy: 'Titik Ukur' },
          secondary: [
            { value: surveyDays, unit: 'days', unitMy: 'hari', label: 'Estimated Survey Days', labelMy: 'Anggaran Hari Ukur' }
          ]
        };
      },
      assumptions: {
        en: '10m grid interval for detailed topographic survey',
        my: 'Jarak grid 10m untuk ukur topografi terperinci'
      }
    }
  },

  foundation: {
    stripFooting: {
      id: 'stripFooting',
      label: 'Strip Footing (M15)',
      labelMy: 'Tapak Jalur (M15)',
      inputType: 'dimensions',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Total Length', labelMy: 'Jumlah Panjang', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Footing Width', labelMy: 'Lebar Tapak', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 18 },
        { id: 'depth', label: 'Footing Depth', labelMy: 'Kedalaman Tapak', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 9 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthMm = convertToMm(inputs.width || 18, units.thickness || 'inch');
        const depthMm = convertToMm(inputs.depth || 9, units.thickness || 'inch');

        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;
        const widthM = widthMm / 1000;
        const depthM = depthMm / 1000;

        const wetVolume = lengthM * widthM * depthM;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M15: 1:2:4 for strip footing
        const cementVolume = (dryVolume / 7) * 1 * wastage;
        const sandVolume = (dryVolume / 7) * 2 * wastage;
        const stoneVolume = (dryVolume / 7) * 4 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³
        const steel = Math.round(lengthM * 3 * 10) / 10;

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Reinforcement', labelMy: 'Tetulang Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M15 Grade (1:2:4), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M15 (1:2:4), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    padFooting: {
      id: 'padFooting',
      label: 'Pad Footing (M20)',
      labelMy: 'Tapak Pad (M20)',
      inputType: 'dimensions',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Pad Length', labelMy: 'Panjang Pad', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 36 },
        { id: 'width', label: 'Pad Width', labelMy: 'Lebar Pad', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 36 },
        { id: 'depth', label: 'Pad Depth', labelMy: 'Kedalaman Pad', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 12 },
        { id: 'quantity', label: 'Number of Pads', labelMy: 'Bilangan Pad', unit: '', unitMy: '', default: 1 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthMm = convertToMm(inputs.length || 36, units.thickness || 'inch');
        const widthMm = convertToMm(inputs.width || 36, units.thickness || 'inch');
        const depthMm = convertToMm(inputs.depth || 12, units.thickness || 'inch');
        const qty = inputs.quantity || 1;

        const lengthM = lengthMm / 1000;
        const widthM = widthMm / 1000;
        const depthM = depthMm / 1000;

        const wetVolume = lengthM * widthM * depthM * qty;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M20: 1:1.5:3 for pad footing
        const cementVolume = (dryVolume / 5.5) * 1 * wastage;
        const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
        const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³
        const steel = Math.round(wetVolume * 80 * 10) / 10;

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Reinforcement', labelMy: 'Tetulang Keluli' }
          ]
        };
      },
      assumptions: {
        en: 'M20 Grade (1:1.5:3), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M20 (1:1.5:3), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    retainingWall: {
      id: 'retainingWall',
      label: 'Retaining Wall (M20)',
      labelMy: 'Dinding Penahan (M20)',
      inputType: 'dimensions',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Wall Length', labelMy: 'Panjang Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'height', label: 'Wall Height', labelMy: 'Tinggi Dinding', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'thickness', label: 'Wall Thickness', labelMy: 'Ketebalan Dinding', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 9 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const heightFt = convertToFeet(inputs.height || 0, units.length || 'ft');
        const thicknessMm = convertToMm(inputs.thickness || 9, units.thickness || 'inch');

        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;
        const heightM = heightFt / CONVERSIONS.M_TO_FT;
        const thicknessM = thicknessMm / 1000;

        const wetVolume = lengthM * heightM * thicknessM;
        const dryVolume = wetVolume * 1.54;
        const wastage = 1.05;

        // M20: 1:1.5:3
        const cementVolume = (dryVolume / 5.5) * 1 * wastage;
        const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
        const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

        const cementBags = Math.ceil(cementVolume * 1440 / 50);
        const sand = Math.round(sandVolume * 100) / 100;
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        const stone = Math.round(stoneVolume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.5 * 100) / 100; // aggregate ~1.5 ton/m³
        const steel = Math.round(wetVolume * 100 * 10) / 10;
        const drainPipe = Math.ceil(lengthM * 1.1);

        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: stone, unit: 'm³', unitMy: 'm³', label: 'Stone/Aggregate (20mm)', labelMy: 'Batu/Agregat (20mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
            { value: steel, unit: 'kg', unitMy: 'kg', label: 'Steel Reinforcement', labelMy: 'Tetulang Keluli' },
            { value: drainPipe, unit: 'm', unitMy: 'm', label: 'Drain Pipe (4")', labelMy: 'Paip Salir (4")' }
          ]
        };
      },
      assumptions: {
        en: 'M20 Grade (1:1.5:3), sand ~1.6 ton/m³, stone ~1.5 ton/m³',
        my: 'Gred M20 (1:1.5:3), pasir ~1.6 tan/m³, batu ~1.5 tan/m³'
      }
    },
    soling: {
      id: 'soling',
      label: 'Stone Soling (Foundation Base)',
      labelMy: 'Batu Soling (Asas Tapak)',
      inputType: 'area',
      unitSelectable: true,
      inputs: [
        { id: 'length', label: 'Length', labelMy: 'Panjang', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'width', label: 'Width', labelMy: 'Lebar', unit: 'ft', unitMy: 'kaki', unitType: 'length' },
        { id: 'thickness', label: 'Soling Thickness', labelMy: 'Ketebalan Soling', unit: 'inch', unitMy: 'inci', unitType: 'thickness', default: 6 }
      ],
      calculate: (inputs, units = {}) => {
        const lengthFt = convertToFeet(inputs.length || 0, units.length || 'ft');
        const widthFt = convertToFeet(inputs.width || 0, units.length || 'ft');
        const thicknessMm = convertToMm(inputs.thickness || 6, units.thickness || 'inch');

        const lengthM = lengthFt / CONVERSIONS.M_TO_FT;
        const widthM = widthFt / CONVERSIONS.M_TO_FT;
        const thicknessM = thicknessMm / 1000;

        // Stone soling volume with 30% voids filled with sand
        const volume = lengthM * widthM * thicknessM * 1.1; // 10% wastage
        const stoneVolume = Math.round(volume * 100) / 100;
        const stoneCuft = Math.round(stoneVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const stoneTon = Math.round(stoneVolume * 1.6 * 100) / 100; // boulder stone ~1.6 ton/m³
        const sandVolume = Math.round(volume * 0.30 * 100) / 100; // 30% sand to fill voids
        const sandCuft = Math.round(sandVolume * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sandVolume * 1.5 * 100) / 100; // sand ~1.5 ton/m³

        return {
          primary: { value: stoneVolume, unit: 'm³', unitMy: 'm³', label: 'Stone (150-200mm)', labelMy: 'Batu (150-200mm)', extra: `${stoneTon} ton / ${stoneCuft} cuft`, extraMy: `${stoneTon} tan / ${stoneCuft} kaki padu` },
          secondary: [
            { value: sandVolume, unit: 'm³', unitMy: 'm³', label: 'Sand (filling voids)', labelMy: 'Pasir (mengisi ruang)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: 'Stone soling 150-200mm (~1.6 ton/m³), sand ~1.5 ton/m³, includes 10% wastage',
        my: 'Batu soling 150-200mm (~1.6 tan/m³), pasir ~1.5 tan/m³, termasuk 10% pembaziran'
      }
    }
  },

  plastering: {
    internalPlaster: {
      id: 'internalPlaster',
      label: 'Internal Plastering',
      labelMy: 'Plaster Dalaman',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Wall Area', labelMy: 'Luas Dinding', unit: 'sqft', unitMy: 'kaki persegi' },
        { id: 'thickness', label: 'Thickness', labelMy: 'Ketebalan', unit: 'mm', unitMy: 'mm', default: 12 }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const thickness = (inputs.thickness || 12) / 1000;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        const volume = areaSqm * thickness * 1.1; // 10% wastage
        // 1:4 ratio for internal
        const cementBags = Math.ceil(volume * (1/5) * 1440 / 50);
        const sand = Math.round(volume * (4/5) * 1.2 * 100) / 100;
        const sandCuft = Math.round(sand * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sand * 1.5 * 100) / 100; // fine sand ~1.5 ton/m³
        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (fine)', labelMy: 'Pasir (halus)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: '1:4 cement-sand ratio, fine sand ~1.5 ton/m³',
        my: 'Nisbah simen-pasir 1:4, pasir halus ~1.5 tan/m³'
      }
    },
    externalPlaster: {
      id: 'externalPlaster',
      label: 'External Rendering',
      labelMy: 'Lepa Luaran',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Wall Area', labelMy: 'Luas Dinding', unit: 'sqft', unitMy: 'kaki persegi' },
        { id: 'thickness', label: 'Thickness', labelMy: 'Ketebalan', unit: 'mm', unitMy: 'mm', default: 20 }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const thickness = (inputs.thickness || 20) / 1000;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        const volume = areaSqm * thickness * 1.1;
        // 1:3 ratio for external (stronger)
        const cementBags = Math.ceil(volume * (1/4) * 1440 / 50);
        const sand = Math.round(volume * (3/4) * 1.2 * 100) / 100;
        const sandCuft = Math.round(sand * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sand * 1.6 * 100) / 100; // coarse sand ~1.6 ton/m³
        // Waterproofing additive
        const waterproofing = Math.round(cementBags * 0.5 * 10) / 10;
        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (coarse)', labelMy: 'Pasir (kasar)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` },
            { value: waterproofing, unit: 'kg', unitMy: 'kg', label: 'Waterproofing Additive', labelMy: 'Bahan Kalis Air' }
          ]
        };
      },
      assumptions: {
        en: '1:3 cement-sand ratio, coarse sand ~1.6 ton/m³',
        my: 'Nisbah simen-pasir 1:3, pasir kasar ~1.6 tan/m³'
      }
    },
    ceilingPlaster: {
      id: 'ceilingPlaster',
      label: 'Ceiling Plastering',
      labelMy: 'Plaster Siling',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Ceiling Area', labelMy: 'Luas Siling', unit: 'sqft', unitMy: 'kaki persegi' },
        { id: 'thickness', label: 'Thickness', labelMy: 'Ketebalan', unit: 'mm', unitMy: 'mm', default: 10 }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const thickness = (inputs.thickness || 10) / 1000;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        const volume = areaSqm * thickness * 1.15; // 15% wastage for overhead work
        // 1:4 ratio
        const cementBags = Math.ceil(volume * (1/5) * 1440 / 50);
        const sand = Math.round(volume * (4/5) * 1.2 * 100) / 100;
        const sandCuft = Math.round(sand * CONVERSIONS.CUM_TO_CUFT * 100) / 100;
        const sandTon = Math.round(sand * 1.5 * 100) / 100; // fine sand ~1.5 ton/m³
        return {
          primary: { value: cementBags, unit: 'bags (50kg)', unitMy: 'beg (50kg)', label: 'Cement', labelMy: 'Simen' },
          secondary: [
            { value: sand, unit: 'm³', unitMy: 'm³', label: 'Sand (fine)', labelMy: 'Pasir (halus)', extra: `${sandTon} ton / ${sandCuft} cuft`, extraMy: `${sandTon} tan / ${sandCuft} kaki padu` }
          ]
        };
      },
      assumptions: {
        en: '1:4 cement-sand ratio, 15% wastage, fine sand ~1.5 ton/m³',
        my: 'Nisbah simen-pasir 1:4, 15% pembaziran, pasir halus ~1.5 tan/m³'
      }
    },
    skimCoat: {
      id: 'skimCoat',
      label: 'Skim Coat',
      labelMy: 'Lapisan Skim',
      inputType: 'area',
      inputs: [
        { id: 'area', label: 'Wall/Ceiling Area', labelMy: 'Luas Dinding/Siling', unit: 'sqft', unitMy: 'kaki persegi' }
      ],
      calculate: (inputs) => {
        const area = inputs.area || 0;
        const areaSqm = area / CONVERSIONS.SQM_TO_SQFT;
        // Skim coat ~1.5kg per sqm per coat, 2 coats
        const skimCoatKg = Math.ceil(areaSqm * 1.5 * 2 * 1.1); // 10% wastage
        const skimCoatBags = Math.ceil(skimCoatKg / 25); // 25kg bags
        return {
          primary: { value: skimCoatBags, unit: 'bags (25kg)', unitMy: 'beg (25kg)', label: 'Skim Coat', labelMy: 'Lapisan Skim' },
          secondary: []
        };
      },
      assumptions: {
        en: 'Ready-mixed skim coat, 2 coats application, ~1.5kg per sqm per coat',
        my: 'Lapisan skim siap campur, 2 lapisan aplikasi, ~1.5kg setiap m² setiap lapisan'
      }
    }
  }
};

/**
 * Calculate with variance range
 */
export const calculateWithRange = (value) => {
  const variance = value * ESTIMATE_VARIANCE;
  return {
    min: Math.ceil(value - variance),
    max: Math.ceil(value + variance),
    avg: Math.ceil(value)
  };
};

/**
 * Format number with commas
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return new Intl.NumberFormat('en-MY').format(num);
};
